/**
 * AiLoader — polite progress indicator for AI-backed calls.
 *
 * <AiLoader busy label="Auto is drafting…" stages={["Reading context","Fetching facts","Composing response"]} />
 *
 * Behaviour:
 *   - Renders nothing when busy=false.
 *   - When busy=true, cycles through the provided `stages` on a 1.4s
 *     tick so the user knows the request is progressing (even if the
 *     LLM is slow).
 *   - Offers a "Cancel & retry" callback surface via `onCancel` — the
 *     component renders a Cancel button when the callback is provided.
 *
 * Props:
 *   busy       (bool)    — whether to render at all
 *   label      (string)  — the primary loading line
 *   stages     (array)   — optional progress narration cycle
 *   onCancel   (fn)      — optional cancel handler
 *   variant    ("inline"|"card") — inline for buttons, card for panels
 *   testid     (string)
 */
import { useEffect, useState } from "react";
import { Loader2, Sparkles, X } from "lucide-react";

const DEFAULT_STAGES = [
  "Reading your context…",
  "Grounding against Canadian rental facts…",
  "Applying safety guardrails…",
  "Composing response…",
];

export default function AiLoader({
  busy = false,
  label = "Working…",
  stages = DEFAULT_STAGES,
  onCancel,
  variant = "card",
  testid = "ai-loader",
}) {
  const [stageIdx, setStageIdx] = useState(0);

  useEffect(() => {
    if (!busy || !stages || stages.length === 0) return undefined;
    setStageIdx(0);
    const iv = setInterval(() => {
      setStageIdx((n) => (n + 1) % stages.length);
    }, 1400);
    return () => clearInterval(iv);
  }, [busy, stages]);

  if (!busy) return null;

  const progressText = stages && stages.length > 0 ? stages[stageIdx] : label;

  if (variant === "inline") {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-slate-600" data-testid={testid}>
        <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
        <span>{progressText}</span>
      </span>
    );
  }

  return (
    <div
      className="p-4 rounded-xl border border-slate-200 bg-white flex items-start gap-3"
      data-testid={testid}
      role="status"
      aria-live="polite"
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
        <Sparkles className="h-4 w-4 animate-pulse" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600" />
          <span className="text-sm font-semibold text-slate-900">{label}</span>
        </div>
        <div className="mt-1.5 text-xs text-slate-500 leading-relaxed">{progressText}</div>
        {stages && stages.length > 1 && (
          <div className="mt-3 flex gap-1" aria-hidden="true">
            {stages.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= stageIdx ? "bg-emerald-500" : "bg-slate-200"
                }`}
              />
            ))}
          </div>
        )}
      </div>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800 text-xs font-semibold"
          data-testid={`${testid}-cancel`}
          aria-label="Cancel"
        >
          <X className="h-3.5 w-3.5" /> Cancel
        </button>
      )}
    </div>
  );
}
